'use client'

import { useState, useCallback } from 'react'
import { toast } from '@/lib/toast'
import { trackMetaPixel } from '@/lib/meta-pixel'
import { getMarketingPlanByTier, getPlanPrice } from '@/config/pricing'

import {
  type BillingPeriod,
  type PlanTier,
  redirectToCheckout,
  BillingError,
  normalizePlanTier,
} from '@/lib/billing'

export type UseCheckoutOptions = {
  onSuccess?: () => void
  onError?: (error: BillingError) => void
}

export type UseCheckoutReturn = {
  checkout: (
    plan: PlanTier | string,
    options?: { newTab?: boolean; billingPeriod?: BillingPeriod }
  ) => Promise<void>
  isLoading: boolean
  loadingPlan: PlanTier | null
  error: BillingError | null
}

export function useCheckout(options: UseCheckoutOptions = {}): UseCheckoutReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [loadingPlan, setLoadingPlan] = useState<PlanTier | null>(null)
  const [error, setError] = useState<BillingError | null>(null)

  const checkout = useCallback(
    async (
      plan: PlanTier | string,
      checkoutOptions?: { newTab?: boolean; billingPeriod?: BillingPeriod }
    ) => {
      const normalizedPlan = normalizePlanTier(plan)

      setIsLoading(true)
      setLoadingPlan(normalizedPlan)
      setError(null)

      try {
        const marketingPlan = getMarketingPlanByTier(normalizedPlan)
        const price = getPlanPrice(marketingPlan, checkoutOptions?.billingPeriod ?? 'monthly')
        trackMetaPixel('InitiateCheckout', {
          plan: normalizedPlan,
          billingPeriod: checkoutOptions?.billingPeriod ?? undefined,
          content_ids: [normalizedPlan],
          content_type: 'product',
          content_name: marketingPlan?.name ?? normalizedPlan,
          value: typeof price === 'number' ? price : undefined,
          currency: 'USD',
        })
        await redirectToCheckout(normalizedPlan, {
          newTab: checkoutOptions?.newTab,
          billingPeriod: checkoutOptions?.billingPeriod,
        })
        options.onSuccess?.()
      } catch (err) {
        const billingError =
          err instanceof BillingError
            ? err
            : new BillingError(
                'Ocurrio un error inesperado. Intenta nuevamente.',
                'unexpected_error'
              )

        setError(billingError)
        options.onError?.(billingError)

        // Show toast notification
        toast.error(billingError.message, {
          description: 'Si el problema persiste, contacta a soporte.',
        })
      } finally {
        setIsLoading(false)
        setLoadingPlan(null)
      }
    },
    [options]
  )

  return {
    checkout,
    isLoading,
    loadingPlan,
    error,
  }
}
